import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { isNative, syncPushTokenWithBackend, clearPushTokenOnBackend, clearBiometricToken } from '../native';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Sessions (Jun 2026): on the web the JWT lives in an httpOnly cookie the
    // browser attaches automatically — localStorage only caches the user /
    // company snapshots for an instant first render. Legacy sessions (and the
    // native shell, which needs the raw token for push/biometrics) still keep
    // a bearer token in localStorage; both paths are accepted by the server.
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedCompany = localStorage.getItem('company');

    // Hydrate from cache first (instant render), then refresh from /users/me.
    if (storedToken) setToken(storedToken);
    // Cookie session: many pages gate their data fetching on `if (token)`.
    // Expose a sentinel so those gates pass; the real JWT lives in the
    // httpOnly cookie and the backend ignores this placeholder bearer value.
    else if (storedUser) setToken('cookie-session');
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch { /* ignore */ }
    }
    if (storedCompany) {
      try { setCompany(JSON.parse(storedCompany)); } catch { /* ignore */ }
    }

    if (storedToken || storedUser) {
      const authHeaders = storedToken ? { Authorization: `Bearer ${storedToken}` } : undefined;

      // Always refresh from the server. This guarantees that newly-added user
      // fields (e.g. is_super_user) are reflected on the first page load after
      // a server-side change, without forcing the user to manually sign out.
      axios.get(`${API}/users/me`, { headers: authHeaders })
        .then((res) => {
          if (res?.data?.user) {
            setUser(res.data.user);
            localStorage.setItem('user', JSON.stringify(res.data.user));
          }
          if (res?.data?.company) {
            setCompany(res.data.company);
            localStorage.setItem('company', JSON.stringify(res.data.company));
          }
        })
        .catch((err) => {
          // 401 means neither the token nor the session cookie is valid —
          // clear and let route guards redirect to /signin.
          if (err?.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('company');
            setToken(null);
            setUser(null);
            setCompany(null);
          }
          // Other failures: keep the cached user object — better than blowing away the session.
        })
        .finally(() => setLoading(false));

      // Server-side schema check — lets a Super User force-refresh every browser
      // for their company via Settings → Security & Compliance.
      axios.get(`${API}/auth/session-schema`, { headers: authHeaders })
        .then((res) => {
          const required = res?.data?.required_schema;
          const stored = localStorage.getItem('ht_session_schema');
          if (required && required !== stored) {
            // Record the new schema version only. The forced "refresh" is
            // already served by the unconditional /users/me refetch above.
            // NEVER remove the cached user here — it is the session marker
            // for httpOnly-cookie sessions (removing it logged users out on
            // their second reload; Jun 2026 theme-audit fix).
            localStorage.setItem('ht_session_schema', required);
          }
        })
        .catch(() => { /* best-effort — don't block app boot */ });
    } else {
      setLoading(false);
    }
  }, []);

  const login = (tokenData, userData, companyData) => {
    setToken(tokenData);
    setUser(userData);
    setCompany(companyData);
    // Web: the server set an httpOnly session cookie on sign-in, so the JWT
    // stays OUT of localStorage (script-injection attacks can't read it).
    // Native shell: keep the token on-device for push/biometric flows.
    if (isNative()) {
      localStorage.setItem('token', tokenData);
    } else {
      localStorage.removeItem('token');
    }
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('company', JSON.stringify(companyData));
    if (isNative()) {
      syncPushTokenWithBackend(BACKEND_URL, tokenData).catch(() => {});
    }
  };

  const logout = async () => {
    // Best-effort cleanup of native side state before we drop the token.
    if (isNative() && token) {
      try { await clearPushTokenOnBackend(BACKEND_URL, token); } catch { /* network may be down */ }
      try { await clearBiometricToken(); } catch { /* keychain may be unavailable */ }
    }
    // Ask the server to clear the httpOnly session cookie.
    try { await axios.post(`${API}/auth/logout`, {}); } catch { /* best-effort */ }
    setToken(null);
    setUser(null);
    setCompany(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('company');
    // Hard-redirect so any in-flight requests are cancelled and the auth-route guard re-evaluates from a clean slate.
    if (typeof window !== 'undefined') {
      window.location.replace('/signin');
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const updateCompany = (companyData) => {
    setCompany(companyData);
    localStorage.setItem('company', JSON.stringify(companyData));
  };

  // Memoized — components use this in useEffect deps. An unstable reference
  // caused an infinite refresh loop on Settings (company kept refreshing →
  // form state reset every render → fields appeared uneditable).
  const refreshCompanyData = useCallback(async () => {
    if (!company?.id) return;

    try {
      const response = await axios.get(`${API}/company/${company.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      updateCompany(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh company data:', error);
      return null;
    }
  }, [company?.id, token]); // eslint-disable-line

  const value = {
    user,
    company,
    token,
    loading,
    login,
    logout,
    updateUser,
    updateCompany,
    refreshCompanyData,
    isAuthenticated: !!token || !!user,
    isSuperUser: user?.is_super_user === true,
    isAdmin: user?.role === 'admin' || user?.is_super_user === true,
    isSupervisor: user?.role === 'supervisor',
    isUser: user?.role === 'user'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
